import { useCallback, useEffect, useRef, useState } from 'react';
import { appendPage, pageRequest, refreshRequest, takePage } from './lanePaging';
import { useBoardLaneRefresh } from './useBoardLaneRefresh';
import { LANE_PAGE_SIZE } from '../../boardWindowing';
import useWindowWidth from '../../useWindowWidth';
import { cardListPageSize, isPhoneWidth } from '../../../shared/utils/phonePaging';
import { onNextUserGesture } from '../../../shared/utils/nextUserGesture';

export interface LanePaging<T> {
  /** undefined while the first page loads (the lane shows its skeleton). */
  items: T[] | undefined;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  setItems: React.Dispatch<React.SetStateAction<T[] | undefined>>;
}

// A My Work board lane loaded a page at a time, the way the ticket board's lanes load: the first page when the lane
// mounts or resetKey changes (Show Closed, My Work / My Group Work), the next as the lane nears its bottom
// (VirtualLane calls loadMore). Refresh (useBoardLaneRefresh) reloads every card already loaded in one request, with
// the cards left on screen, and swaps them in place - so the lane keeps its scroll position, as the ticket board's
// Refresh does. A response that a newer load has overtaken is dropped.
export const useLanePaging = <T>(
  fetchPage: (skip: number, top: number) => Promise<T[]>,
  keyOf: (item: T) => string | number,
  resetKey: string
): LanePaging<T> => {
  const [items, setItems] = useState<T[] | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { refreshKey, takeRefresh, done } = useBoardLaneRefresh();
  // A phone's lanes load as its other card lists do; a width that crosses the breakpoint starts the lane over.
  const pageSize = cardListPageSize(isPhoneWidth(useWindowWidth()), LANE_PAGE_SIZE);
  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;

  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  const keyOfRef = useRef(keyOf);
  keyOfRef.current = keyOf;
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;
  const loadingMoreRef = useRef(false);
  // A next page that failed to load. VirtualLane asks again whenever the loading state changes while the lane is at
  // its bottom, so asking at once looped for as long as the server failed; it waits for the user instead.
  const [loadFailed, setLoadFailed] = useState(false);
  const loadFailedRef = useRef(false);
  const latest = useRef(0);

  useEffect(() => {
    const isRefresh = takeRefresh();
    const request = ++latest.current;
    loadingMoreRef.current = false;
    setIsLoadingMore(false);
    loadFailedRef.current = false;
    setLoadFailed(false);
    if (!isRefresh) setItems(undefined);

    const { skip, top, size } = isRefresh ? refreshRequest(itemsRef.current?.length ?? 0, pageSize) : { ...pageRequest(0, pageSize), size: pageSize };
    fetchRef.current(skip, top)
      .then(raw => {
        if (request !== latest.current) return;
        const { page, hasMore: more } = takePage(raw, size);
        setItems(page);
        setHasMore(more);
      })
      .catch(error => {
        if (request !== latest.current) return;
        console.error('Error loading a My Work lane', error);
        setItems(prev => prev ?? []);
        setHasMore(false);
      })
      .finally(() => { if (isRefresh) done(refreshKey); });
  }, [resetKey, refreshKey, pageSize]);

  const loadMore = useCallback(() => {
    const current = itemsRef.current;
    if (!current || !hasMoreRef.current || loadingMoreRef.current || loadFailedRef.current) return;
    loadingMoreRef.current = true;
    setIsLoadingMore(true);
    const request = latest.current;

    const { skip, top } = pageRequest(current.length, pageSizeRef.current);
    fetchRef.current(skip, top)
      .then(raw => {
        if (request !== latest.current) return;
        const { page, hasMore: more } = takePage(raw, pageSizeRef.current);
        setItems(prev => appendPage(prev ?? [], page, keyOfRef.current));
        setHasMore(more);
      })
      .catch(error => {
        if (request !== latest.current) return;
        console.error('Error loading more of a My Work lane', error);
        loadFailedRef.current = true;
        setLoadFailed(true);
      })
      .finally(() => {
        if (request !== latest.current) return;
        loadingMoreRef.current = false;
        setIsLoadingMore(false);
      });
  }, []);

  useEffect(() => {
    if (!loadFailed) return;
    return onNextUserGesture(() => {
      loadFailedRef.current = false;
      setLoadFailed(false);
      loadMore();
    });
  }, [loadFailed, loadMore]);

  return { items, hasMore, isLoadingMore, loadMore, setItems };
};
